import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Input } from "@/components/ui/input";

export type MultiSelectOption = { value: string; label: string };

/**
 * Checkbox dropdown for dashboard filters: alphabetical, searchable, and
 * multi-select. Written here rather than on Radix because the backoffice has
 * no popover primitive and this needs none — a button, an absolutely
 * positioned panel, and a click-outside listener.
 *
 * Sorting is `nb` locale-aware, so æ/ø/å land after z where a Norwegian
 * reader looks for them rather than where their code points fall.
 */
export function MultiSelect({
  id,
  options,
  selected,
  onChange,
  placeholder,
  searchPlaceholder = "Søk…",
  className,
}: {
  /** Optional control ID so a caller can attach a visible label. */
  id?: string;
  options: MultiSelectOption[];
  /** Empty means "all" — the caller decides what that implies for its query. */
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  searchPlaceholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const sorted = useMemo(
    () => [...options].sort((a, b) => a.label.localeCompare(b.label, "nb")),
    [options],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("nb");
    if (!q) return sorted;
    return sorted.filter((o) => o.label.toLocaleLowerCase("nb").includes(q));
  }, [sorted, query]);

  // A selection the caller has dropped from `options` (filtering companies by
  // product does exactly that) disappears from the summary and the list, but
  // it stays in `selected` and comes back on the next `onChange`. The caller
  // owns that cleanup — see the effect in b2b-economics.tsx.
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const chosen = useMemo(
    () => sorted.filter((o) => selectedSet.has(o.value)),
    [sorted, selectedSet],
  );

  const toggle = (value: string) => {
    onChange(
      selectedSet.has(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };

  const summary =
    chosen.length === 0
      ? placeholder
      : chosen.length === 1
        ? chosen[0]!.label
        : `${chosen.length} valgt`;

  return (
    <div ref={root} className={cn("relative", className)}>
      <button
        id={id}
        aria-expanded={open}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-admin-card-border bg-admin-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-admin-primary-focus",
          chosen.length === 0
            ? "text-admin-text-subtle"
            : "text-admin-text-primary",
        )}
      >
        <span className="truncate">{summary}</span>
        <span className="flex items-center gap-1 shrink-0">
          {chosen.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Nullstill"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }}
              className="rounded p-0.5 text-admin-text-subtle hover:text-admin-text-primary"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-admin-text-subtle" />
        </span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[16rem] rounded-md border border-admin-card-border bg-admin-card shadow-lg">
          <div className="p-2">
            {id && <label htmlFor={`${id}-search`} className="mb-1 block text-xs text-admin-text-subtle">Søk</label>}
            <Input
              id={id ? `${id}-search` : undefined}
              aria-label={searchPlaceholder}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8"
            />
          </div>
          <div className="max-h-64 overflow-y-auto pb-1">
            {visible.length === 0 && (
              <p className="px-3 py-2 text-xs text-admin-text-subtle">
                Ingen treff
              </p>
            )}
            {visible.map((o) => {
              const isSelected = selectedSet.has(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-admin-text-primary hover:bg-admin-card-border/40"
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                      isSelected
                        ? "border-admin-primary-focus bg-admin-primary-focus/20"
                        : "border-admin-card-border",
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
